import { NetworkStatus, useQuery } from "@apollo/client";
import {
  AccountCard,
  AssetRuleSettingsDropdown,
  BreadcrumbArray,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  entryFromEventFragment,
  LabelPill,
  PrimaryFullScreenSpinner,
  TimeLog,
  Txt,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type React from "react";
import { Suspense } from "react";
import {
  PiArrowsLeftRight,
  PiBellSimpleRinging,
  PiCalendar,
  PiLightning,
} from "react-icons/pi";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type {
  AssetCardFieldsFragment,
  BeneficiaryAssetCardFieldsFragment,
  OpsRuleDetailFragment,
} from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/asset-rules_/$ruleId")({
  head: () => opsHead("Rule Detail"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment OpsRuleDetail on AssetRule {
    ...AssetRuleSettingsFields
    id
    name
    status
    evaluationStatus
    evaluationCadence
    nextEvaluationDueAt
    lastEvaluatedAt
    events {
      ...TimeLogEventEntry
    }
    asset {
      ...AssetCardFields
      businessEntity {
        organisation {
          id
          name
        }
      }
      estimatedValueWithOpenFundMovements {
        amount
        currency
      }
    }
    ... on AlertAssetRule {
      minimumBalanceThreshold {
        amount
        currency
      }
      maximumBalanceThreshold {
        amount
        currency
      }
    }
    ... on TopUpAssetRule {
      balanceThreshold {
        currency
        amount
      }
      balanceTarget {
        currency
        amount
      }
      sourceAssets {
        ...AssetCardFields
      }
    }
    ... on RecurringWithdrawalAssetRule {
      amount {
        amount
        currency
      }
      beneficiaryAccount {
        ...BeneficiaryAssetCardFields
        organisation {
          id
          name
        }
      }
    }
    ... on SweepAssetRule {
      maximumBalanceThreshold {
        currency
        amount
      }
      balanceTarget {
        currency
        amount
      }
      destinationAsset {
        ...AssetCardFields
      }
      destinationBeneficiaryAccount {
        ...BeneficiaryAssetCardFields
      }
    }
  }
`);

const ruleDetailDocument = graphql(`
  query AdminAssetRuleDetail($ruleId: ID!) {
    node(id: $ruleId) {
      ... on AssetRule {
        ...OpsRuleDetail
      }
    }
  }
`);

const ruleHistoryDocument = graphql(`
  query AdminAssetRuleHistory($ruleId: ID!, $first: Int!, $after: String) {
    node(id: $ruleId) {
      ... on AlertAssetRule {
        id
        alertHistory: history(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          totalCount
          history {
            id
            evaluatedAt
            result
            alertRuleUserTask {
              id
            }
          }
        }
      }
      ... on TopUpAssetRule {
        id
        topUpHistory: history(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          totalCount
          history {
            id
            evaluatedAt
            result
            assetRebalance {
              id
              transferGroup {
                id
              }
            }
          }
        }
      }
      ... on RecurringWithdrawalAssetRule {
        id
        recurringHistory: history(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          totalCount
          history {
            id
            evaluatedAt
            result
            assetRebalance {
              id
              transferGroup {
                id
              }
            }
          }
        }
      }
      ... on SweepAssetRule {
        id
        sweepHistory: history(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          totalCount
          history {
            id
            evaluatedAt
            result
            assetRebalance {
              id
              transferGroup {
                id
              }
            }
          }
        }
      }
    }
  }
`);

function ruleTypeIcon(typename: string) {
  switch (typename) {
    case "TopUpAssetRule":
      return PiLightning;
    case "RecurringWithdrawalAssetRule":
      return PiCalendar;
    case "SweepAssetRule":
      return PiArrowsLeftRight;
    default:
      return PiBellSimpleRinging;
  }
}

function ruleTypeLabel(typename: string): string {
  switch (typename) {
    case "AlertAssetRule":
      return "Alert";
    case "TopUpAssetRule":
      return "Auto Top-up";
    case "RecurringWithdrawalAssetRule":
      return "Recurring Withdrawal";
    case "SweepAssetRule":
      return "Auto Sweep";
    default:
      return prettyPrintEnum(typename);
  }
}

function activityResultLabel(result: string): string {
  switch (result) {
    case "NO_ACTION_REQUIRED":
      return "No action required";
    case "EXISTING_TRANSFERS_IN_PROGRESS":
      return "Existing transfers in progress";
    case "INSUFFICIENT_FUNDS":
      return "Insufficient funds";
    case "ASSET_REBALANCE_CREATED":
      return "Transfer created";
    case "NEW_REMINDER_CREATED":
      return "Reminder task created";
    case "EXISTING_REMINDER_EXISTS":
      return "Open task already exists";
    default:
      return prettyPrintEnum(result);
  }
}

type ActivityItem = {
  id: string;
  evaluatedAt: Date;
  result: string;
  transferGroupId?: string;
};

const HISTORY_PAGE_SIZE = 10;

function RuleConditionFields({
  rule,
}: {
  rule: OpsRuleDetailFragment;
}): React.ReactNode {
  if (rule.__typename === "AlertAssetRule") {
    return (
      <>
        {rule.minimumBalanceThreshold && (
          <div className="flex flex-col gap-1">
            <Txt variant="caption" tone="secondary">
              Minimum balance
            </Txt>
            <CurrencyAmountUI
              amount={rule.minimumBalanceThreshold.amount}
              currency={rule.minimumBalanceThreshold.currency}
              nonCopyable
            />
          </div>
        )}
        {rule.maximumBalanceThreshold && (
          <div className="flex flex-col gap-1">
            <Txt variant="caption" tone="secondary">
              Maximum balance
            </Txt>
            <CurrencyAmountUI
              amount={rule.maximumBalanceThreshold.amount}
              currency={rule.maximumBalanceThreshold.currency}
              nonCopyable
            />
          </div>
        )}
      </>
    );
  }

  if (rule.__typename === "TopUpAssetRule") {
    return (
      <>
        {rule.balanceThreshold && (
          <div className="flex flex-col gap-1">
            <Txt variant="caption" tone="secondary">
              Top-up threshold
            </Txt>
            <CurrencyAmountUI
              amount={rule.balanceThreshold.amount}
              currency={rule.balanceThreshold.currency}
              nonCopyable
            />
          </div>
        )}
        {rule.balanceTarget && (
          <div className="flex flex-col gap-1">
            <Txt variant="caption" tone="secondary">
              Top-up target
            </Txt>
            <CurrencyAmountUI
              amount={rule.balanceTarget.amount}
              currency={rule.balanceTarget.currency}
              nonCopyable
            />
          </div>
        )}
      </>
    );
  }

  if (rule.__typename === "RecurringWithdrawalAssetRule" && rule.amount) {
    return (
      <div className="flex flex-col gap-1">
        <Txt variant="caption" tone="secondary">
          Withdrawal amount
        </Txt>
        <CurrencyAmountUI
          amount={rule.amount.amount}
          currency={rule.amount.currency}
          nonCopyable
        />
      </div>
    );
  }

  if (rule.__typename === "SweepAssetRule") {
    return (
      <>
        {rule.maximumBalanceThreshold && (
          <div className="flex flex-col gap-1">
            <Txt variant="caption" tone="secondary">
              Sweep when balance exceeds
            </Txt>
            <CurrencyAmountUI
              amount={rule.maximumBalanceThreshold.amount}
              currency={rule.maximumBalanceThreshold.currency}
              nonCopyable
            />
          </div>
        )}
        {rule.balanceTarget && (
          <div className="flex flex-col gap-1">
            <Txt variant="caption" tone="secondary">
              Sweep target balance
            </Txt>
            <CurrencyAmountUI
              amount={rule.balanceTarget.amount}
              currency={rule.balanceTarget.currency}
              nonCopyable
            />
          </div>
        )}
      </>
    );
  }

  return null;
}

function FundingPrioritySources({
  rule,
}: {
  rule: OpsRuleDetailFragment;
}): React.ReactNode {
  if (rule.__typename !== "TopUpAssetRule") {
    return null;
  }

  const sources = rule.sourceAssets;

  if (!sources || sources.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Funding source priority</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col divide-y divide-separator rounded-md border">
          {sources.map((asset, index) => (
            <div key={asset.id} className="flex items-center gap-2 px-3 py-2">
              <div className="w-5 shrink-0">
                <Txt variant="body" tone="tertiary">
                  {index + 1}
                </Txt>
              </div>
              <div className="min-w-0 flex-1">
                <AccountCard
                  account={asset as AssetCardFieldsFragment}
                  subText="BUSINESS_ENTITY"
                  linkTo={`/assets/${asset.id}`}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <Txt variant="body" tone="tertiary">
            Default priority will be used after the above selections
          </Txt>
        </div>
      </CardContent>
    </Card>
  );
}

function ActivityCard({ ruleId }: { ruleId: string }) {
  const { data, loading, fetchMore, networkStatus } = useQuery(
    ruleHistoryDocument,
    {
      variables: {
        ruleId,
        first: HISTORY_PAGE_SIZE,
        after: null,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const node = data?.node;

  let historyItems: ActivityItem[] = [];
  let hasNextPage = false;
  let endCursor: string | null | undefined;

  if (node?.__typename === "TopUpAssetRule" && node.topUpHistory) {
    historyItems = node.topUpHistory.history.map((h) => ({
      id: h.id,
      evaluatedAt: h.evaluatedAt,
      result: h.result,
      transferGroupId: h.assetRebalance?.transferGroup?.id ?? undefined,
    }));
    hasNextPage = node.topUpHistory.pageInfo.hasNextPage;
    endCursor = node.topUpHistory.pageInfo.endCursor;
  } else if (node?.__typename === "AlertAssetRule" && node.alertHistory) {
    historyItems = node.alertHistory.history.map((h) => ({
      id: h.id,
      evaluatedAt: h.evaluatedAt,
      result: h.result,
    }));
    hasNextPage = node.alertHistory.pageInfo.hasNextPage;
    endCursor = node.alertHistory.pageInfo.endCursor;
  } else if (
    node?.__typename === "RecurringWithdrawalAssetRule" &&
    node.recurringHistory
  ) {
    historyItems = node.recurringHistory.history.map((h) => ({
      id: h.id,
      evaluatedAt: h.evaluatedAt,
      result: h.result,
      transferGroupId: h.assetRebalance?.transferGroup?.id ?? undefined,
    }));
    hasNextPage = node.recurringHistory.pageInfo.hasNextPage;
    endCursor = node.recurringHistory.pageInfo.endCursor;
  } else if (node?.__typename === "SweepAssetRule" && node.sweepHistory) {
    historyItems = node.sweepHistory.history.map((h) => ({
      id: h.id,
      evaluatedAt: h.evaluatedAt,
      result: h.result,
      transferGroupId: h.assetRebalance?.transferGroup?.id ?? undefined,
    }));
    hasNextPage = node.sweepHistory.pageInfo.hasNextPage;
    endCursor = node.sweepHistory.pageInfo.endCursor;
  }

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  const historyColumns: ColumnDef<ActivityItem>[] = [
    {
      id: "date",
      accessorKey: "evaluatedAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date" />
      ),
      cell: ({ row }) => (
        <DateUI date={row.original.evaluatedAt} variant="DATETIME" delta />
      ),
    },
    {
      id: "result",
      accessorKey: "result",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Result" />
      ),
      cell: ({ row }) => {
        const label = activityResultLabel(row.original.result);
        if (row.original.transferGroupId) {
          return (
            <Link
              to="/transfer-groups/$transferGroupId"
              params={{ transferGroupId: row.original.transferGroupId }}
              className="underline"
            >
              {label}
            </Link>
          );
        }
        return <span>{label}</span>;
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {!loading && !historyItems.length ? (
          <Txt variant="description">
            No recent activity. Rule evaluations will appear here once the
            automation runs.
          </Txt>
        ) : (
          <>
            <DataTable
              isLoadingData={loading || isFetchingMore}
              data={historyItems}
              columns={historyColumns}
              manualPagination
              hidePagination
              className="overflow-hidden"
            />
            <div className="mt-4 flex w-full flex-row justify-center">
              {hasNextPage ? (
                <Button
                  variant="outline"
                  loading={isFetchingMore}
                  onClick={() =>
                    fetchMore({
                      variables: {
                        ruleId,
                        first: HISTORY_PAGE_SIZE,
                        after: endCursor,
                      },
                    })
                  }
                >
                  Load more
                </Button>
              ) : (
                !!historyItems.length && (
                  <p className="text-secondary text-sm">No more data</p>
                )
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function View() {
  const { ruleId } = useParams({
    from: "/__auth/asset-rules_/$ruleId",
  });
  const navigate = useNavigate();

  const { data, loading } = useQuery(ruleDetailDocument, {
    variables: { ruleId },
  });

  const node = data?.node;
  const rule = node && "name" in node ? (node as OpsRuleDetailFragment) : null;

  useOpsTitle(rule?.name ? `${rule.name} - Rule` : "Rule Detail");

  if (loading) {
    return <PrimaryFullScreenSpinner />;
  }

  if (!rule) {
    throw new NotFoundError();
  }

  const organisation =
    rule.asset?.businessEntity?.organisation ??
    (rule.__typename === "RecurringWithdrawalAssetRule"
      ? rule.beneficiaryAccount?.organisation
      : undefined);

  const asset = rule.asset as AssetCardFieldsFragment | undefined;
  const isActive = rule.status === "ACTIVE";
  const RuleIcon = ruleTypeIcon(rule.__typename ?? "");

  return (
    <AdminPageContainer>
      <BreadcrumbArray
        stack={[
          {
            to: "/asset-rules",
            title: "Asset Rules",
          },
          ...(organisation
            ? [
                {
                  to: `/organisations/${organisation.id}`,
                  title: organisation.name,
                },
              ]
            : []),
          {
            title: rule.name,
          },
        ]}
      />
      <AdminPageHeader
        title={rule.name}
        buttons={
          <AssetRuleSettingsDropdown
            rule={rule}
            organisationId={organisation?.id ?? ""}
            onDeleted={() => navigate({ to: "/asset-rules" })}
          />
        }
      />
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Rule details</CardTitle>
            <LabelPill
              label={isActive ? "Active" : "Inactive"}
              variant={isActive ? "success" : "neutral"}
            />
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <div className="grid grid-cols-4 gap-x-4 gap-y-6">
              <div className="flex flex-col gap-1">
                <Txt variant="caption" tone="secondary">
                  Type
                </Txt>
                <div className="flex items-center gap-1.5">
                  <RuleIcon size={16} className="text-tertiary" />
                  <Txt variant="name">
                    {ruleTypeLabel(rule.__typename ?? "")}
                  </Txt>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Txt variant="caption" tone="secondary">
                  Evaluation cadence
                </Txt>
                <Txt variant="name">
                  {prettyPrintEnum(rule.evaluationCadence)}
                </Txt>
              </div>
              {rule.evaluationStatus && (
                <div className="flex flex-col gap-1">
                  <Txt variant="caption" tone="secondary">
                    Evaluation status
                  </Txt>
                  <Txt variant="name">
                    {prettyPrintEnum(rule.evaluationStatus)}
                  </Txt>
                </div>
              )}
              <RuleConditionFields rule={rule} />

              {rule.asset && (
                <>
                  <div className="flex flex-col gap-1">
                    <Txt variant="caption" tone="secondary">
                      Current balance
                    </Txt>
                    <CurrencyAmountUI
                      amount={rule.asset.estimatedValue?.amount}
                      currency={rule.asset.estimatedValue?.currency}
                      nonCopyable
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Txt variant="caption" tone="secondary">
                      Projected balance
                    </Txt>
                    <CurrencyAmountUI
                      amount={
                        rule.asset.estimatedValueWithOpenFundMovements?.amount
                      }
                      currency={
                        rule.asset.estimatedValueWithOpenFundMovements?.currency
                      }
                      nonCopyable
                    />
                  </div>
                </>
              )}

              {asset && (
                <div className="col-start-1 w-fit shrink-0 self-center">
                  {rule.__typename === "SweepAssetRule" && (
                    <Txt variant="caption" tone="secondary">
                      Sweep from
                    </Txt>
                  )}
                  <AccountCard
                    account={asset}
                    subText={["BUSINESS_ENTITY", "BALANCE"]}
                    linkTo={`/assets/${asset.id}`}
                  />
                </div>
              )}
              {!asset &&
                rule.__typename === "RecurringWithdrawalAssetRule" &&
                rule.beneficiaryAccount && (
                  <div className="col-start-1 w-fit shrink-0 self-center">
                    <AccountCard
                      account={
                        rule.beneficiaryAccount as BeneficiaryAssetCardFieldsFragment
                      }
                      subText={["BUSINESS_ENTITY", "BALANCE"]}
                      linkTo={`/beneficiary-accounts/${rule.beneficiaryAccount.id}`}
                    />
                  </div>
                )}
              {rule.__typename === "SweepAssetRule" &&
                (rule.destinationAsset ||
                  rule.destinationBeneficiaryAccount) && (
                  <div className="flex w-fit flex-col gap-1 self-center">
                    <Txt variant="caption" tone="secondary">
                      Sweep to
                    </Txt>
                    {rule.destinationAsset ? (
                      <AccountCard
                        account={
                          rule.destinationAsset as AssetCardFieldsFragment
                        }
                        subText={["BUSINESS_ENTITY", "BALANCE"]}
                        linkTo={`/assets/${rule.destinationAsset.id}`}
                      />
                    ) : (
                      <AccountCard
                        account={
                          rule.destinationBeneficiaryAccount as BeneficiaryAssetCardFieldsFragment
                        }
                        subText={["BUSINESS_ENTITY"]}
                        linkTo={`/beneficiary-accounts/${rule.destinationBeneficiaryAccount?.id}`}
                      />
                    )}
                  </div>
                )}
              <div
                className={`${rule.__typename === "SweepAssetRule" && (rule.destinationAsset || rule.destinationBeneficiaryAccount) ? "col-span-2" : "col-span-3"} flex flex-col items-start gap-1`}
              >
                <Txt variant="caption" tone="secondary">
                  Next evaluation
                </Txt>
                <DateUI
                  date={rule.nextEvaluationDueAt}
                  variant="DATETIME"
                  delta
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <FundingPrioritySources rule={rule} />

        <ActivityCard ruleId={ruleId} />

        <Card>
          <CardHeader>
            <CardTitle>Event log</CardTitle>
          </CardHeader>
          <CardContent>
            {rule.events && rule.events.length > 0 ? (
              <TimeLog entries={rule.events.map(entryFromEventFragment)} />
            ) : (
              <Txt>No events</Txt>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageContainer>
  );
}
